import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControlLabel,
  Slider,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import { Save as SaveIcon, Streetview as StreetViewIcon } from '@mui/icons-material';
import {
  CAMERA_STREET_VIEW_DEFAULTS,
  clearCameraStreetViewCache,
  updateCameraStreetViewSettings,
  useCameraStreetViewSettings,
  type CameraStreetViewSettings,
} from '../../services/cameraStreetViewService';

const formatBytes = (n: number) => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

/**
 * Admin: Street View for camera locations. Master switch, inline previews
 * (needs GOOGLE_MAPS_API_KEY on the server), and how many "cameras in view"
 * thumbnails the map filmstrip may request at once.
 */
const CameraStreetViewAdmin: React.FC = () => {
  const { settings, loading, error: loadError, refresh } = useCameraStreetViewSettings();
  const [draft, setDraft] = useState<CameraStreetViewSettings>(settings ?? CAMERA_STREET_VIEW_DEFAULTS);
  const [saving, setSaving] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const clearCache = async (negativesOnly: boolean) => {
    const what = negativesOnly ? 'the "no imagery" entries' : 'all cached Street View thumbnails';
    if (!window.confirm(`Clear ${what}? Affected cameras will be re-fetched from Google on next view.`)) return;
    setClearing(true);
    setError(null);
    setSuccess(null);
    try {
      const next = await clearCameraStreetViewCache(negativesOnly);
      setSuccess(
        negativesOnly
          ? 'Negative entries cleared; those spots will be re-checked next time they are viewed.'
          : `Thumbnail cache cleared${next.cache ? ` (${next.cache.images} images remain)` : ''}.`,
      );
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to clear Street View cache');
    } finally {
      setClearing(false);
    }
  };

  useEffect(() => {
    if (settings) setDraft(settings);
  }, [settings]);

  const dirty =
    !!settings &&
    (draft.enabled !== settings.enabled ||
      draft.embed !== settings.embed ||
      draft.in_view_limit !== settings.in_view_limit);

  const save = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await updateCameraStreetViewSettings({
        enabled: draft.enabled,
        embed: draft.embed,
        in_view_limit: draft.in_view_limit,
      });
      setSuccess(
        draft.enabled
          ? draft.embed && draft.embed_available
            ? 'Saved. Camera popups now show a Street View thumbnail and link; the "cameras in view" strip is available on the maps.'
            : 'Saved. Camera popups now show a Street View link (links only — no inline previews).'
          : 'Saved. Street View is hidden for all users.',
      );
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to save Street View settings');
    } finally {
      setSaving(false);
    }
  };

  const keyChip = draft.embed_available ? (
    <Chip size="small" color="success" variant="outlined" label="Google key configured" />
  ) : (
    <Tooltip title="Set GOOGLE_MAPS_API_KEY on the backend to enable inline thumbnails. Links work without it.">
      <Chip size="small" color="warning" variant="outlined" label="No Google key on server — links only" />
    </Tooltip>
  );

  return (
    <Card variant="outlined" sx={{ mb: 3 }}>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <StreetViewIcon color={draft.enabled ? 'primary' : 'disabled'} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600, flex: 1 }}>
            Street View for cameras
          </Typography>
          {keyChip}
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Lets analysts see what a camera sees. When on, camera popups on Pathfinder and COP get a{' '}
          <strong>Street View</strong> link aimed along the camera&apos;s mapped direction. With a Google key
          on the server, popups also show an inline thumbnail and the maps gain a <strong>cameras in view</strong>{' '}
          strip. The key never leaves the server; images are proxied and cached.
        </Typography>

        {loadError && (
          <Alert severity="warning" sx={{ mb: 2 }} onClose={() => void refresh()}>
            {loadError}
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        <Stack spacing={1.5}>
          <FormControlLabel
            control={
              <Switch
                checked={draft.enabled}
                disabled={loading || saving}
                onChange={(e) => setDraft((d) => ({ ...d, enabled: e.target.checked }))}
              />
            }
            label={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>Enable Street View</Typography>
                <Typography variant="caption" color="text.secondary">
                  Master switch. Off hides every Street View link, thumbnail and map tool for all users.
                </Typography>
              </Box>
            }
          />
          <FormControlLabel
            control={
              <Switch
                checked={draft.embed}
                disabled={loading || saving || !draft.enabled}
                onChange={(e) => setDraft((d) => ({ ...d, embed: e.target.checked }))}
              />
            }
            label={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>Inline previews</Typography>
                <Typography variant="caption" color="text.secondary">
                  Show a Street View still inside camera popups and the cameras-in-view strip.
                  {!draft.embed_available && ' Needs GOOGLE_MAPS_API_KEY on the server; until then only links show.'}
                </Typography>
              </Box>
            }
          />
          <Box sx={{ pl: 1, pr: 2, opacity: draft.enabled && draft.embed ? 1 : 0.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Cameras in view: max thumbnails — {draft.in_view_limit}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Caps how many Street View images one map view may request at a time (billing guard).
            </Typography>
            <Slider
              value={draft.in_view_limit}
              min={4}
              max={30}
              step={1}
              marks={[{ value: 4, label: '4' }, { value: 12, label: '12' }, { value: 20, label: '20' }, { value: 30, label: '30' }]}
              disabled={loading || saving || !draft.enabled || !draft.embed}
              onChange={(_, v) => setDraft((d) => ({ ...d, in_view_limit: Array.isArray(v) ? v[0] : v }))}
              valueLabelDisplay="auto"
              sx={{ mt: 1 }}
            />
          </Box>
        </Stack>

        {settings?.cache && (
          <Box
            sx={{
              mt: 2,
              p: 1.25,
              borderRadius: 1,
              bgcolor: 'action.hover',
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1,
            }}
          >
            <Box sx={{ flex: 1, minWidth: 220 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Thumbnail cache
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {settings.cache.available
                  ? `${settings.cache.images.toLocaleString()} image${settings.cache.images === 1 ? '' : 's'} · ${formatBytes(
                      settings.cache.bytes,
                    )} · ${settings.cache.negatives.toLocaleString()} no-imagery · ${settings.cache.hits.toLocaleString()} hit${
                      settings.cache.hits === 1 ? '' : 's'
                    } served. Stored in Postgres (camera_street_view_images); each view is fetched from Google once, then reused for a year (30 days for no-imagery).`
                  : 'Cache table unavailable — thumbnails are only cached in memory until the next restart.'}
              </Typography>
            </Box>
            <Button
              size="small"
              variant="outlined"
              disabled={clearing || !settings.cache.available || settings.cache.negatives === 0}
              onClick={() => void clearCache(true)}
            >
              Re-check no-imagery
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="warning"
              disabled={clearing || !settings.cache.available || (settings.cache.entries ?? 0) === 0}
              onClick={() => void clearCache(false)}
            >
              {clearing ? 'Clearing…' : 'Clear cache'}
            </Button>
          </Box>
        )}

        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={loading || saving || !dirty}
            onClick={() => void save()}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
          <Button variant="text" disabled={loading || saving || !dirty} onClick={() => settings && setDraft(settings)}>
            Reset
          </Button>
          {settings?.updated_at && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
              Updated {new Date(settings.updated_at).toLocaleString()}
              {settings.updated_by ? ` by ${settings.updated_by}` : ''}
            </Typography>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default CameraStreetViewAdmin;
